import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Tab, Tabs } from "@mui/material"
import type { FC, SyntheticEvent } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import {
  CARRIER_PREFERENCE_TABS,
  CARRIER_PREFERENCE_TYPE_LABELS,
  DEFAULT_CARRIER_PREFERENCES_DATA,
} from "../../constants/carrierPreferences"
import { useGetTransportationCompanies } from "../../hooks/useGetTransportationCompanies"
import { useSaveCarrierPreferences } from "../../hooks/useSaveCarrierPreferences"
import { carrierPreferencesSchema } from "../../schemas/carrierPreferences"
import type { CarrierPreferencesData } from "../../types/carrierPreferences"
import { CarrierPreferenceForm } from "../CarrierPreferenceForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import {
  CarrierType,
  type LocationCarrierPreference,
  type LocationCarrierPreferenceItemInput,
} from "@/graphql/codegen/graphql"

interface EditCarrierPreferencesDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  locationId: string
  initialData?: LocationCarrierPreference[]
}

export const EditCarrierPreferencesDialog: FC<EditCarrierPreferencesDialogProps> = ({
  onClose,
  locationId,
  initialData = [],
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const { saveCarrierPreferences, isSavingCarrierPreferences } = useSaveCarrierPreferences()
  const { companies, isFetching: isFetchingCompanies } = useGetTransportationCompanies()

  const isEditMode = initialData.length > 0
  const dialogTitle = isEditMode ? "Edit Carrier Preferences" : "Add Carrier Preferences"

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const defaultValues: CarrierPreferencesData = { ...DEFAULT_CARRIER_PREFERENCES_DATA }

  // Populate with initial data if provided
  initialData.forEach((pref) => {
    defaultValues[pref.carrierType] = {
      carrierId: pref.carrierId || "",
      accountNumber: pref.accountNumber || "",
      freightTerm: pref.freightTerms || "",
      note: pref.note || "",
      comment: pref.comment || "",
      customerPaysForSampleShipping: pref.customerPaysForSampleShipping ?? false,
    }
  })

  const methods = useForm<CarrierPreferencesData>({
    defaultValues,
    resolver: zodResolver(carrierPreferencesSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
    control,
    watch,
  } = methods

  const handleCloseClick = () => {
    if (isDirty) {
      setShowCancelDialog(true)
    } else {
      onClose(false)
    }
  }

  const handleConfirmClose = () => {
    setShowCancelDialog(false)
    onClose(false)
  }

  const onSubmit = async (data: CarrierPreferencesData) => {
    const preferences: LocationCarrierPreferenceItemInput[] = Object.entries(data)
      .filter(([_, value]) => Boolean(value.carrierId))
      .map(([type, value]) => {
        const carrierType = type as CarrierType

        return {
          carrierType,
          carrierId: value.carrierId || undefined,
          accountNumber: value.accountNumber || undefined,
          freightTerms: value.freightTerm || undefined,
          note: value.note || undefined,
          comment: value.comment || undefined,
          customerPaysForSampleShipping:
            carrierType === CarrierType.Sample ? (value.customerPaysForSampleShipping ?? false) : undefined,
        }
      })

    const success = await saveCarrierPreferences({
      shipToLocationId: locationId,
      preferences,
    })

    if (success) {
      onClose(true)
    }
  }

  const hasAnyData = CARRIER_PREFERENCE_TABS.some((tabType) => {
    const tabData = watch(tabType)
    return Boolean(tabData?.carrierId)
  })

  return (
    <>
      <Dialog
        open
        onClose={handleCloseClick}
        maxWidth={false}
        sx={{ "& .MuiDialog-paper": { maxWidth: "700px", width: "100%" } }}
        data-testid='carrier-preferences-dialog'
      >
        <DialogHeader title={dialogTitle} onClose={handleCloseClick} />

        <DialogContent className='flex w-full flex-col px-10 pb-1 pt-8'>
          <FormProvider {...methods}>
            <Box>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant='scrollable'
                scrollButtons='auto'
                data-testid='carrier-preferences-tabs'
              >
                {CARRIER_PREFERENCE_TABS.map((tabType, index) => (
                  <Tab
                    key={tabType}
                    label={CARRIER_PREFERENCE_TYPE_LABELS[tabType]}
                    value={index}
                    sx={{ textTransform: "none" }}
                    data-test='carrier-preference-tab'
                  />
                ))}
              </Tabs>
            </Box>
            <Box className='mt-6'>
              <CarrierPreferenceForm
                key={CARRIER_PREFERENCE_TABS[activeTab]}
                tabType={CARRIER_PREFERENCE_TABS[activeTab]}
                control={control}
                watch={watch}
                companies={companies}
                loadingCompanies={isFetchingCompanies}
              />
            </Box>
          </FormProvider>
        </DialogContent>

        <DialogActions className='justify-between p-8'>
          <Button appearance='outlined' onClick={handleCloseClick} data-testid='carrier-preferences-cancel-button'>
            Cancel
          </Button>
          <Button
            appearance='contained'
            onClick={handleSubmit(onSubmit)}
            disabled={!hasAnyData || isSubmitting || isSavingCarrierPreferences}
            data-testid='carrier-preferences-save-button'
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {showCancelDialog && <CancelDialog onClose={() => setShowCancelDialog(false)} onConfirm={handleConfirmClose} />}
    </>
  )
}
